(() => {
  if (window.__bstStockistInstalled) return;
  window.__bstStockistInstalled = true;

  const STOCKIST_SOURCE_PAGE_CANDIDATES = ['/pages/filialen', '/pages/store-locator'];

  const HANDLE_OVERRIDES = {
    // Nur falls Legacy-Slug != Shopify-Handle:
    // 'legacy-handle': '/pages/shopify-handle'
  };

  const previousResultsReceived = window.__stockist_widget_resultsreceived;
  const previousLocationSelected = window.__stockist_widget_location_selected;
  const previousDomLoaded = window.__stockist_widget_domloaded;
  const previousListChanged = window.__stockist_widget_listchanged;
  const previousMapCreated = window.__stockist_widget_mapcreated;

  let currentLocationActivated = false;
  let activationTimer = null;
  let activationBurstPrimed = false;
  let themeBurstPrimed = false;
  let themeStabilizationTimer = null;
  let themeStabilizationAttempts = 0;
  let stockistRootElement = null;
  let stockistSourceMarkupPromise = null;
  const locationDetailCache = new Map();
  const activationBurstDelays = [100, 500, 1200, 2400, 4200, 6500];
  const stockistThemeId = 'bst-stockist-theme';
  const zoomOverlayVisibleClass = 'bst-stockist-zoom-overlay--visible';
  const stockistBluePinIcon = createSvgDataUri('#004f9e');
  const stockistDarkPinIcon = createSvgDataUri('#626262');
  const stockistLightPinIcon = createSvgDataUri('#ffffff');

  function createSvgDataUri(fill) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 13"><path fill="${fill}" d="M5 0C2.239 0 0 2.239 0 5c0 3.198 5 8 5 8s5-4.802 5-8c0-2.761-2.239-5-5-5zm0 7.2A2.2 2.2 0 1 1 5 2.8a2.2 2.2 0 0 1 0 4.4z"/></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  }

  function getMainPageRoot() {
    return document.querySelector('.bst-main-page');
  }

  function getConfiguredSourcePages() {
    return (getMainPageRoot()?.dataset.stockistSourcePages || '')
      .split(',')
      .map((candidate) => candidate.trim())
      .filter(Boolean);
  }

  function getStockistSourceCandidates() {
    return [...new Set([...getConfiguredSourcePages(), ...STOCKIST_SOURCE_PAGE_CANDIDATES])];
  }

  function isMobileStockistViewport() {
    return Math.max(320, document.documentElement.clientWidth || window.innerWidth) < 990;
  }

  function getCurrentPageHandle() {
    const match = window.location.pathname.match(/^\/pages\/([^/?#]+)/i);
    return match ? match[1].toLowerCase() : null;
  }

  function isDetailLocationPage(handle) {
    return !!handle && /^\d{4}-/.test(handle);
  }

  function getCurrentDetailPath() {
    const handle = getCurrentPageHandle();
    if (!isDetailLocationPage(handle)) return null;
    return `/pages/${handle}`;
  }

  function normalizePath(path) {
    if (!path) return null;
    return path.replace(/[?#].*$/, '').replace(/\/+$/, '');
  }

  function extractStockistSourceMarkup(doc) {
    if (!doc) return null;

    const stockistWidget = doc.querySelector('stockist-store-locator');
    if (!stockistWidget) return null;

    return {
      stockistWidget: stockistWidget.cloneNode(true),
      stockistScriptSrc: doc.querySelector('script[src*="stockist.co/embed/"]')?.src || null
    };
  }

  async function fetchStockistSourceMarkup(path) {
    const response = await fetch(path, { credentials: 'same-origin' });
    if (!response.ok) return null;

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return extractStockistSourceMarkup(parsed);
  }

  async function loadStockistSourceMarkup() {
    if (stockistSourceMarkupPromise) {
      return stockistSourceMarkupPromise;
    }

    stockistSourceMarkupPromise = (async () => {
      const currentDocumentMarkup = extractStockistSourceMarkup(document);
      if (currentDocumentMarkup) {
        return currentDocumentMarkup;
      }

      for (const path of getStockistSourceCandidates()) {
        const normalizedPath = normalizePath(path);

        if (!normalizedPath || normalizedPath === normalizePath(window.location.pathname)) {
          continue;
        }

        const markup = await fetchStockistSourceMarkup(path);
        if (markup) {
          return markup;
        }
      }

      throw new Error('Stockist widget markup missing on configured source pages');
    })().catch((error) => {
      stockistSourceMarkupPromise = null;
      throw error;
    });

    return stockistSourceMarkupPromise;
  }

  function toShopifyFilialeUrl(rawUrl) {
    if (!rawUrl) return null;

    try {
      const url = new URL(rawUrl, window.location.origin);

      if (url.pathname.startsWith('/pages/')) {
        return `${url.pathname}${url.search}${url.hash}`;
      }

      const match = url.pathname.match(/^\/de\/filialen\/([^/]+)\/?$/i);
      if (!match) return rawUrl;

      const handle = match[1].toLowerCase();
      return HANDLE_OVERRIDES[handle] || `/pages/${handle}`;
    } catch (error) {
      console.warn('Stockist URL rewrite failed', error);
      return rawUrl;
    }
  }

  function rewriteLegacyFilialeLinks(scope = document) {
    scope.querySelectorAll('a[href*="/de/filialen/"]').forEach((link) => {
      const currentHref = link.getAttribute('href');
      const rewrittenHref = toShopifyFilialeUrl(currentHref);

      if (rewrittenHref && rewrittenHref !== currentHref) {
        link.setAttribute('href', rewrittenHref);
      }
    });
  }

  function rewriteResultsPayload(payload) {
    if (!payload || !Array.isArray(payload.results)) return payload;

    const currentDetailPath = getCurrentDetailPath();
    const rewrittenResults = payload.results.map((location) => ({
      ...location,
      website: toShopifyFilialeUrl(location.website)
    }));

    if (!currentDetailPath) {
      return {
        ...payload,
        results: rewrittenResults
      };
    }

    const currentIndex = rewrittenResults.findIndex(
      (location) => normalizePath(location.website) === currentDetailPath
    );

    if (currentIndex <= 0) {
      return {
        ...payload,
        results: rewrittenResults
      };
    }

    const currentLocation = rewrittenResults[currentIndex];
    const reorderedResults = rewrittenResults.slice();
    reorderedResults.splice(currentIndex, 1);
    reorderedResults.unshift(currentLocation);

    return {
      ...payload,
      results: reorderedResults
    };
  }

  function ensureStockistScript(src) {
    if (!src) return;
    if (document.querySelector(`script[src="${src}"]`)) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.body.appendChild(script);
  }

  function getStockistRootElement() {
    if (stockistRootElement?.isConnected) return stockistRootElement;

    stockistRootElement = getMainPageRoot()?.querySelector('.bst-stockist-locator')
      || getMainPageRoot()?.querySelector('stockist-store-locator')?.parentElement
      || null;

    return stockistRootElement;
  }

  function getStockistWidgetHost() {
    return getStockistRootElement()?.querySelector('stockist-store-locator') || null;
  }

  function getStockistClient() {
    return window.Stockist?.__widget?._client || null;
  }

  function getStockistLayoutRoot() {
    return window.Stockist?.__widget?._map?._rootElement?.parentElement || null;
  }

  function normalizeStockistContainers() {
    const outerRoot = getStockistRootElement();
    const widgetHost = getStockistWidgetHost();

    if (outerRoot) {
      outerRoot.style.setProperty('display', 'block', 'important');
      outerRoot.style.removeProperty('grid-template-columns');
      outerRoot.style.removeProperty('grid-template-rows');
      outerRoot.style.removeProperty('flex-direction');
      outerRoot.style.setProperty('width', '100%', 'important');
      outerRoot.style.setProperty('height', 'auto', 'important');
      outerRoot.style.setProperty('max-width', 'none', 'important');
      outerRoot.style.setProperty('min-width', '0', 'important');
      outerRoot.style.setProperty('background', '#fff', 'important');
    }

    if (widgetHost) {
      widgetHost.style.setProperty('display', 'block', 'important');
      widgetHost.style.setProperty('width', '100%', 'important');
      widgetHost.style.setProperty('height', 'auto', 'important');
      widgetHost.style.setProperty('max-width', 'none', 'important');
      widgetHost.style.setProperty('min-width', '0', 'important');
    }
  }

  function getLeafletMapInstance() {
    return window.Stockist?.__widget?._map?._map || null;
  }

  function showVisitorLocationOnMap(position) {
    const latitude = position?.coords?.latitude ?? position?.latitude;
    const longitude = position?.coords?.longitude ?? position?.longitude;
    const map = getLeafletMapInstance();
    const Leaflet = window.L;

    if (!map || !Leaflet || typeof latitude !== 'number' || typeof longitude !== 'number') {
      return false;
    }

    const latLng = Leaflet.latLng(latitude, longitude);

    if (window.__bstVisitorLocationMarker && typeof map.removeLayer === 'function') {
      try {
        map.removeLayer(window.__bstVisitorLocationMarker);
      } catch (error) {
        console.warn('Failed to remove previous visitor marker', error);
      }
    }

    const marker = Leaflet.circleMarker(latLng, {
      radius: 8,
      color: '#ffffff',
      weight: 3,
      fillColor: '#004f9e',
      fillOpacity: 1
    });

    marker.addTo(map);
    marker.bindPopup('Aktueller Standort', {
      autoClose: false,
      className: 'bst-stockist-visitor-popup',
      closeButton: true,
      closeOnClick: false,
      offset: [0, -12]
    });
    window.__bstVisitorLocationMarker = marker;
    focusCurrentLocationOnMap(map, latLng);
    marker.openPopup();
    return true;
  }

  function getCurrentBrowserPosition(options = {}) {
    if (!navigator.geolocation) {
      return Promise.reject(new Error('Geolocation unsupported'));
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 120000,
        ...options
      });
    });
  }

  async function triggerCurrentLocationSearch() {
    const client = getStockistClient();
    const geolocateTask = typeof client?.geolocate === 'function'
      ? Promise.resolve(client.geolocate()).catch((error) => {
        console.warn('Stockist geolocate failed', error);
        return null;
      })
      : null;

    const position = await getCurrentBrowserPosition();
    await geolocateTask;
    showVisitorLocationOnMap(position);

    // Re-run theme/ui sync after Stockist potentially re-renders the list.
    window.setTimeout(() => {
      scheduleStockistThemeStabilization(true);
    }, 250);

    return true;
  }

  function getLeafletMapContainer() {
    const map = getLeafletMapInstance();
    return map?.getContainer?.() || null;
  }

  function createAutoLocatorSection() {
    const pageRoot = getMainPageRoot();
    const pageContent = pageRoot?.querySelector('.bst-main-page__content');
    if (!pageRoot || !pageContent) return null;

    const currentHandle = getCurrentPageHandle();
    const isDetailPage = isDetailLocationPage(currentHandle);

    const existingSection = pageContent.querySelector('.section--store-locator');
    if (existingSection) {
      const existingMount = existingSection.querySelector('[data-bst-stockist-mount], store-locator[data-component]');
      if (existingMount) return existingMount;

      const existingWrapper =
        existingSection.querySelector('.section__content--fullwidth')
        || existingSection.querySelector('.section__wrapper')
        || existingSection;

      const mount = document.createElement('div');
      mount.dataset.bstStockistMount = 'auto';
      existingWrapper.appendChild(mount);
      return mount;
    }

    const section = document.createElement('section');
    section.className = `section section--store-locator bst-location-stockist${isDetailPage ? ' bst-location-stockist--detail' : ''}`;

    section.innerHTML = `
      <div class="section__wrapper">
        <div class="section__content section__content--fullwidth">
          <div class="bst-location-stockist__intro">
            <span class="bst-location-stockist__eyebrow">Filialfinder</span>
            <h2 class="bst-location-stockist__title">${isDetailPage ? 'Diese Filiale auf der Karte' : 'Alle Filialen auf einen Blick'}</h2>
            <p class="bst-location-stockist__text">${isDetailPage
        ? 'Die aktuelle Filiale wird nach dem Laden automatisch markiert und in die Karte gezoomt.'
        : 'Finde die passende Filiale, springe direkt auf die Karte und wechsle von dort bequem in die jeweilige Detailseite.'}</p>
          </div>
          <div class="bst-location-stockist__mount" data-bst-stockist-mount="auto"></div>
        </div>
      </div>
    `;

    const firstSection = pageContent.querySelector('.section');

    if (isDetailPage && firstSection) {
      firstSection.insertAdjacentElement('afterend', section);
    } else {
      pageContent.appendChild(section);
    }

    return section.querySelector('[data-bst-stockist-mount]');
  }

  function getLegacyLocatorMount() {
    return getMainPageRoot()?.querySelector('[data-bst-stockist-mount], store-locator[data-component]') || null;
  }

  function ensureLocatorMount() {
    const currentHandle = getCurrentPageHandle();
    const existingMount = getLegacyLocatorMount();
    if (existingMount) return existingMount;

    if (currentHandle === 'filialen' || currentHandle === 'store-locator' || isDetailLocationPage(currentHandle)) {
      return createAutoLocatorSection();
    }

    return null;
  }

  function mountStockistWidget(target, widgetShell) {
    if (!target || !widgetShell) return;

    if (target.matches('store-locator[data-component]')) {
      target.replaceWith(widgetShell);
      return;
    }

    target.replaceChildren(widgetShell);
  }

  function getZoomHintText() {
    return navigator.platform.toLowerCase().includes('mac')
      ? 'Verwende Cmd+Scrollen zum Zoomen der Karte'
      : 'Verwende Strg+Scrollen zum Zoomen der Karte';
  }

  function ensureZoomHint(container) {
    if (!container) return null;

    let hint = container.querySelector('.bst-stockist-zoom-overlay');

    if (!hint) {
      hint = document.createElement('div');
      hint.className = 'bst-stockist-zoom-overlay';
      hint.textContent = getZoomHintText();
      container.appendChild(hint);
    }

    return hint;
  }

  function showZoomHint(container) {
    const hint = ensureZoomHint(container);
    if (!hint) return;

    window.clearTimeout(container.__bstZoomHintHideTimer);
    hint.classList.add(zoomOverlayVisibleClass);

    container.__bstZoomHintHideTimer = window.setTimeout(() => {
      hint.classList.remove(zoomOverlayVisibleClass);
    }, 1200);
  }

  function hideZoomHint(container) {
    const hint = container?.querySelector('.bst-stockist-zoom-overlay');
    if (!hint) return;

    window.clearTimeout(container.__bstZoomHintHideTimer);
    hint.classList.remove(zoomOverlayVisibleClass);
  }

  function ensureScrollWheelModifierGuard() {
    const map = getLeafletMapInstance();
    const container = getLeafletMapContainer();
    if (!map || !container || !map.scrollWheelZoom) return false;

    ensureZoomHint(container);

    if (container.dataset.bstScrollGuardInstalled === 'true') {
      return true;
    }

    container.dataset.bstScrollGuardInstalled = 'true';
    map.scrollWheelZoom.disable();

    const disableMapWheelZoom = () => {
      window.clearTimeout(container.__bstZoomDisableTimer);
      if (map.scrollWheelZoom.enabled()) {
        map.scrollWheelZoom.disable();
      }
    };

    const scheduleWheelZoomDisable = () => {
      window.clearTimeout(container.__bstZoomDisableTimer);
      container.__bstZoomDisableTimer = window.setTimeout(disableMapWheelZoom, 220);
    };

    container.addEventListener('wheel', (event) => {
      const wantsMapZoom = event.ctrlKey || event.metaKey;

      if (!wantsMapZoom) {
        disableMapWheelZoom();
        showZoomHint(container);
        return;
      }

      hideZoomHint(container);

      if (!map.scrollWheelZoom.enabled()) {
        map.scrollWheelZoom.enable();
      }

      scheduleWheelZoomDisable();
    }, { capture: true, passive: true });

    container.addEventListener('mouseleave', disableMapWheelZoom);
    window.addEventListener('blur', disableMapWheelZoom);

    return true;
  }

  function ensureStockistTheme() {
    normalizeStockistContainers();

    const root = getStockistLayoutRoot();
    if (!root) return false;
    const isMobileViewport = isMobileStockistViewport();

    if (!root.querySelector(`style[data-${stockistThemeId}]`)) {
      const style = document.createElement('style');
      style.setAttribute(`data-${stockistThemeId}`, 'true');
      style.textContent = `
        .stockist-layout.stockist-layout-desktop {
          display: grid !important;
          grid-template-columns: 462px minmax(0, 1fr);
          grid-template-rows: auto auto;
          align-items: stretch;
          width: 100% !important;
          min-height: 0;
          overflow: hidden;
          isolation: isolate;
          background: #fff;
        }

        .stockist-search-form {
          grid-column: 1 / -1 !important;
          grid-row: 1 !important;
          display: flex !important;
          align-items: center;
          justify-content: flex-start;
          gap: 16px;
          width: 100% !important;
          min-height: 68px;
          padding: 12px 16px;
          overflow: hidden;
          box-sizing: border-box;
          background: var(--bst-color-gray-900, #262626);
          color: #fff;
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
        }

        .stockist-search-form::after {
          content: none;
        }

        .stockist-search-fields-horizontal {
          display: flex !important;
          align-items: center;
          flex: 1 1 auto !important;
          gap: 10px;
          width: 100% !important;
          max-width: none !important;
        }

        .stockist-search-query {
          flex: 0 1 33% !important;
          width: 33% !important;
          max-width: 33% !important;
        }

        .stockist-search-wrapper,
        .stockist-search-field {
          flex: 1 1 auto !important;
          width: 100% !important;
          max-width: 100% !important;
        }

        .stockist-search-field input {
          width: 100% !important;
          height: 44px !important;
          min-height: 44px !important;
          border: 0;
          border-radius: var(--bst-radius-md, 7px);
          box-shadow: none;
          box-sizing: border-box;
          color: var(--bst-color-gray-900, #262626);
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
          font-size: 15px;
          font-weight: 400;
          line-height: 1.2;
          padding: 0 14px;
        }

        .stockist-search-field input::placeholder {
          color: var(--bst-color-gray-800, #626262);
          opacity: 1;
        }

        .stockist-search-submit {
          flex: 0 0 148px !important;
          width: 148px !important;
          margin-left: 0;
        }

        .stockist-search-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 148px !important;
          min-width: 148px !important;
          height: 44px;
          padding: 0 18px;
          border: 0;
          border-radius: var(--bst-radius-md, 7px);
          background: var(--bst-color-blue-500, #004f9e);
          box-shadow: none;
          color: transparent;
          font-size: 0;
          overflow: hidden;
          cursor: pointer;
        }

        .stockist-search-button > * {
          display: none !important;
        }

        .stockist-search-button::after {
          content: 'SIE SUCHEN...';
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          white-space: nowrap;
          color: #fff;
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0;
          text-transform: uppercase;
        }

        .stockist-search-form .stockist-search-by-location,
        .bst-stockist-search-location-hint {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          flex: 0 0 auto;
          margin: 0 0 0 12px;
          padding: 0;
          color: rgba(255, 255, 255, 0.85) !important;
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
          font-size: 14px;
          font-weight: 500;
          line-height: 1.2;
          text-decoration: none !important;
          white-space: nowrap;
          border: 0;
          background: transparent;
          cursor: pointer;
          appearance: none;
        }

        .bst-stockist-search-location-hint[aria-disabled='true'] {
          opacity: 0.7;
          pointer-events: none;
        }

        .stockist-search-form .stockist-search-by-location::before,
        .bst-stockist-search-location-hint::before {
          content: '';
          flex: 0 0 auto;
          width: 10px;
          height: 13px;
          background: center / contain no-repeat ${stockistLightPinIcon};
          opacity: 0.85;
        }

        .stockist-search-form .stockist-search-by-location .stockist-icon,
        .stockist-search-form .stockist-search-by-location svg {
          display: none !important;
        }

        .stockist-search-by-location.bst-stockist-location-trigger--label-fallback::after {
          content: attr(data-bst-location-label);
          display: inline;
          color: inherit;
          font: inherit;
        }

        .stockist-result-panel {
          grid-column: 1 / 2 !important;
          grid-row: 2 / 3 !important;
          min-height: 0;
          height: 850px;
          overflow: hidden !important;
          background: #fff;
        }

        .stockist-results {
          height: 100% !important;
          min-height: 0;
          overflow: hidden !important;
        }

        .stockist-result-list {
          height: 100% !important;
          max-height: 100% !important;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          overscroll-behavior: contain;
          padding: 0 8px 0 0;
        }

        .stockist-result-list > ul {
          margin: 0;
          padding: 0;
        }

        .stockist-map {
          grid-column: 2 / 3 !important;
          grid-row: 2 / 3 !important;
          position: relative;
          width: 100% !important;
          height: 850px !important;
          min-height: 850px !important;
          overflow: hidden;
        }

        .stockist-map-inner,
        .stockist-map-inner.leaflet-container,
        .stockist-map .leaflet-container {
          height: 100% !important;
          overflow: hidden !important;
        }

        .stockist-map .leaflet-pane,
        .stockist-map .leaflet-map-pane,
        .stockist-map .leaflet-tile-pane,
        .stockist-map .leaflet-marker-pane {
          overflow: visible !important;
        }

        .leaflet-popup.bst-stockist-visitor-popup {
          margin-bottom: 16px !important;
        }

        .leaflet-popup.bst-stockist-visitor-popup .leaflet-popup-content-wrapper {
          padding: 0 !important;
          border-radius: 0 !important;
          box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16) !important;
        }

        .leaflet-popup.bst-stockist-visitor-popup .leaflet-popup-content {
          min-width: 0 !important;
          margin: 0 !important;
          padding: 14px 34px 14px 14px !important;
          color: #626262 !important;
          font-family: "Work Sans", Helvetica, Arial, sans-serif !important;
          font-size: 13px !important;
          font-weight: 400 !important;
          line-height: 1.2 !important;
          white-space: nowrap !important;
        }

        .leaflet-popup.bst-stockist-visitor-popup .leaflet-popup-close-button {
          top: 8px !important;
          right: 8px !important;
          width: 16px !important;
          height: 16px !important;
          padding: 0 !important;
          color: #9f9f9f !important;
          font-size: 18px !important;
          line-height: 16px !important;
        }

        .leaflet-popup.bst-stockist-visitor-popup .leaflet-popup-close-button:hover,
        .leaflet-popup.bst-stockist-visitor-popup .leaflet-popup-close-button:focus-visible {
          color: #626262 !important;
          text-decoration: none !important;
        }

        .leaflet-popup.bst-stockist-visitor-popup .leaflet-popup-tip-container {
          margin-top: -1px !important;
        }

        .leaflet-popup.bst-stockist-visitor-popup .leaflet-popup-tip {
          box-shadow: -4px 4px 14px rgba(0, 0, 0, 0.08) !important;
        }

        .bst-stockist-zoom-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          pointer-events: none;
          padding: 24px;
          background: rgba(38, 38, 38, 0.42);
          color: #fff;
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
          font-size: 16px;
          font-weight: 500;
          line-height: 1.25;
          text-align: center;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.2s ease, visibility 0.2s ease;
        }

        .bst-stockist-zoom-overlay.${zoomOverlayVisibleClass} {
          opacity: 1;
          visibility: visible;
        }

        .stockist-result-status,
        .stockist-result-distance,
        .stockist-result-addr-country,
        .stockist-result-directions-link,
        .stockist-result-view-on-map-link,
        .leaflet-preview-control,
        .leaflet-preview-dismiss {
          display: none !important;
        }

        .stockist-layout .stockist-result-list .stockist-list-result {
          position: relative;
          list-style: none;
          margin: 0 !important;
          padding: 26px 0 0 0 !important;
          min-height: 0 !important;
          border: 0 !important;
          border-top: 1px solid #ececec !important;
          background: #fff !important;
          box-shadow: none !important;
          box-sizing: border-box;
        }

        .stockist-layout .stockist-result-list > ul > .stockist-list-result:last-child {
          border-bottom: 1px solid #ececec !important;
        }

        .stockist-layout .stockist-result-list .stockist-list-result::after {
          content: '';
          position: absolute;
          top: 31px;
          right: 22px;
          width: 9px;
          height: 9px;
          border-right: 1.5px solid #004f9e;
          border-bottom: 1.5px solid #004f9e;
          transform: rotate(45deg);
          transform-origin: center;
          transition: transform 0.25s ease, top 0.25s ease, border-color 0.25s ease;
        }

        .stockist-layout .stockist-result-list .stockist-list-result.stockist-selected {
          background: #fff !important;
          border-top-color: #ececec !important;
          box-shadow: none !important;
        }

        .stockist-layout .stockist-result-list .stockist-list-result.stockist-selected::after {
          top: 36px;
          border-right-color: #004f9e;
          border-bottom-color: #004f9e;
          transform: rotate(-135deg);
        }

        .stockist-layout .stockist-result-list .stockist-result-name {
          color: #004f9e !important;
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
          font-size: 20px !important;
          font-weight: 500 !important;
          line-height: 1.15 !important;
          margin: 0 0 10px !important;
        }

        .stockist-layout .stockist-result-list .stockist-result-name a {
          color: #004f9e !important;
          text-decoration: none !important;
        }

        .stockist-layout .stockist-result-list .stockist-result-name a:hover,
        .stockist-layout .stockist-result-list .stockist-result-name a:focus-visible {
          text-decoration: none !important;
        }

        .stockist-layout .stockist-result-list .stockist-result-address {
          color: #004f9e !important;
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
          font-size: 15px !important;
          line-height: 1.45 !important;
          margin: 0 0 14px !important;
        }

        .stockist-layout .stockist-result-list .stockist-result-address .bst-stockist-pin-link {
          color: inherit !important;
          display: inline-block !important;
          padding-left: 28px !important;
          position: relative;
          text-decoration: underline !important;
          text-underline-offset: 2px;
        }

        .stockist-layout .stockist-result-list .stockist-result-address .bst-stockist-pin-link::before {
          content: '';
          position: absolute;
          top: 2px;
          left: 0;
          width: 14px;
          height: 18px;
          background: center / contain no-repeat ${stockistBluePinIcon};
          opacity: 1;
        }

        .stockist-layout .stockist-result-list .stockist-result-address .bst-stockist-pin-link:hover,
        .stockist-layout .stockist-result-list .stockist-result-address .bst-stockist-pin-link:focus-visible {
          text-decoration: underline !important;
        }

        .stockist-layout .stockist-result-list .stockist-result-addr-1,
        .stockist-layout .stockist-result-list .stockist-result-addr-locality {
          display: inline;
        }

        .stockist-layout .stockist-result-list .stockist-result-addr-1::after {
          content: ', ';
        }

        .stockist-layout .stockist-result-list .stockist-result-addr-2,
        .stockist-layout .stockist-result-list .stockist-result-addr-country {
          display: none !important;
        }

        .stockist-layout .stockist-result-list .stockist-result-details {
          display: block;
          margin: 0;
          padding: 0;
          list-style: none;
          background: transparent;
        }

        .stockist-layout .stockist-result-list .stockist-result-details::before {
          content: 'Kontakt:';
          display: block;
          margin-bottom: 6px;
          color: #262626;
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
          font-size: 17px !important;
          font-weight: 600 !important;
          line-height: 1.35 !important;
        }

        .stockist-layout .stockist-result-list .stockist-result-details > li {
          margin: 0;
          display: flex;
          align-items: flex-start;
          gap: 8px;
          color: #262626 !important;
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
          font-size: 16px !important;
          line-height: 1.45 !important;
        }

        .stockist-layout .stockist-result-list .stockist-result-details > li::before {
          content: none !important;
          display: none !important;
        }

        .stockist-layout .stockist-result-list .bst-stockist-detail-label {
          flex: 0 0 72px;
          color: #262626 !important;
          white-space: nowrap;
        }

        .stockist-layout .stockist-result-list .bst-stockist-detail-value {
          flex: 1 1 auto;
          min-width: 0;
        }

        .stockist-layout .stockist-result-list .stockist-result-phone a {
          color: #004f9e !important;
          text-decoration: underline !important;
          text-underline-offset: 2px;
        }

        .stockist-layout .stockist-result-list .stockist-result-email a {
          color: #262626 !important;
          text-decoration: none !important;
        }

        .stockist-layout .stockist-result-list .stockist-result-website {
          margin-top: 8px;
          display: block;
        }

        .stockist-layout .stockist-result-list .stockist-result-website::before {
          display: none;
        }

        .stockist-layout .stockist-result-list .stockist-result-website a {
          color: #262626 !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          text-decoration: none !important;
        }

        .stockist-layout .stockist-result-list .bst-stockist-hours {
          margin-top: 16px;
          padding: 14px 10px 10px !important;
          background: #ececec !important;
        }

        .stockist-layout .stockist-result-list .bst-stockist-hours__title {
          display: block;
          margin-bottom: 8px;
          color: #262626;
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
          font-size: 17px !important;
          font-weight: 600 !important;
          line-height: 1.35 !important;
        }

        .stockist-layout .stockist-result-list .bst-stockist-hours__row {
          display: flex;
          gap: 8px;
          color: #262626 !important;
          font-family: "Work Sans", Helvetica, Arial, sans-serif;
          font-size: 16px !important;
          line-height: 1.45 !important;
        }

        .stockist-layout .stockist-result-list .bst-stockist-hours__label {
          flex: 0 0 52px;
          color: #262626 !important;
          white-space: nowrap;
        }

        .stockist-layout .stockist-result-list .bst-stockist-hours__value {
          color: #262626 !important;
        }

        .stockist-map .leaflet-control-compact-attribution,
        .stockist-map .stockist-powered-by-link,
        .stockist-powered-by-link {
          display: none !important;
        }

        @media screen and (max-width: 989px) {
          .stockist-layout.stockist-layout-desktop,
          .stockist-layout.stockist-layout-mobile {
            display: flex !important;
            flex-direction: column;
            min-height: 0;
          }

          .stockist-search-form {
            order: 1;
            display: flex !important;
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
            position: relative;
            min-height: 0;
            padding: 16px;
            background: var(--bst-color-gray-900, #262626);
            color: #fff;
            font-family: "Work Sans", Helvetica, Arial, sans-serif;
          }

          .stockist-search-form:not(.bst-stockist-search-form--has-location)::before {
            content: none;
            display: none;
          }

          .stockist-search-form:not(.bst-stockist-search-form--has-location)::after {
            content: none;
            display: none;
          }

          .stockist-search-fields-horizontal {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) minmax(136px, 45%);
            gap: 10px;
            width: 100%;
            padding: 0;
          }

          .stockist-search-query,
          .stockist-search-submit {
            width: auto;
            margin: 0 !important;
          }

          .stockist-search-submit {
            min-width: 0;
          }

          .stockist-search-field input {
            height: 44px !important;
            min-height: 44px !important;
            border-radius: var(--bst-radius-md, 7px);
            font-size: 15px;
            padding: 0 14px;
          }

          .stockist-search-button {
            position: relative;
            width: 100%;
            min-width: 0;
            height: 44px;
            background: var(--bst-color-blue-500, #004f9e);
            border: 0;
            border-radius: var(--bst-radius-md, 7px);
            box-shadow: none;
            color: transparent;
            font-size: 0;
          }

          .stockist-search-button > * {
            display: none !important;
          }

          .stockist-search-button::after {
            content: 'SIE SUCHEN...';
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            color: #fff;
            font-family: "Work Sans", Helvetica, Arial, sans-serif;
            font-size: 13px;
            font-weight: 600;
            letter-spacing: 0;
            text-transform: uppercase;
            white-space: nowrap;
          }

          .stockist-search-button .stockist-search-button-text {
            display: none !important;
          }

          .stockist-search-button .stockist-icon,
          .stockist-search-button svg,
          .stockist-search-button [class*='icon'] {
            display: none !important;
          }

          .stockist-search-by-location {
            display: flex !important;
            align-items: center;
            gap: 8px;
            width: 100%;
            min-height: 24px;
            margin: 0;
            color: #fff !important;
            font-family: "Work Sans", Helvetica, Arial, sans-serif;
            font-size: 14px;
            font-weight: 500;
            line-height: 1.2;
          }

          .stockist-search-by-location::before,
          .bst-stockist-search-location-hint::before {
            content: '';
            flex: 0 0 auto;
            width: 10px;
            height: 13px;
            background: center / contain no-repeat ${stockistLightPinIcon};
            opacity: 0.85;
          }

          .stockist-search-by-location .stockist-icon,
          .stockist-search-by-location svg,
          .bst-stockist-search-location-hint .stockist-icon,
          .bst-stockist-search-location-hint svg {
            display: none !important;
          }

          .stockist-search-by-location,
          .stockist-search-by-location a,
          .stockist-search-by-location button,
          .stockist-search-by-location span,
          .bst-stockist-search-location-hint {
            color: inherit !important;
            font: inherit !important;
            text-decoration: none !important;
          }

          .stockist-search-by-location.bst-stockist-location-trigger--label-fallback::after {
            content: attr(data-bst-location-label);
            display: inline;
          }

          .stockist-search-by-location button,
          .bst-stockist-search-location-hint {
            padding: 0 !important;
            border: 0 !important;
            background: transparent !important;
          }

          .stockist-result-panel {
            order: 3;
            height: auto !important;
            max-height: none !important;
            min-height: 0 !important;
            overflow: visible !important;
          }

          .stockist-results,
          .stockist-result-list {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            padding-right: 0;
          }

          .stockist-map {
            order: 2;
            height: min(76vw, 34rem) !important;
            min-height: 26rem !important;
            margin-top: 12px;
            border-radius: var(--bst-radius-md, 7px);
            background: var(--bst-color-gray-200, #f2f2f2);
            overflow: hidden;
          }

          .stockist-map-inner,
          .stockist-map-inner.leaflet-container,
          .stockist-map .leaflet-container {
            border-radius: inherit;
          }

          .stockist-layout .stockist-result-list .stockist-list-result {
            padding: 24px 28px 18px 20px !important;
          }

          .stockist-layout .stockist-result-list .stockist-list-result::after {
            top: 30px;
            right: 18px;
          }
        }

        @media screen and (max-width: 749px) {
          .stockist-search-fields-horizontal {
            grid-template-columns: minmax(0, 1fr);
          }

          .stockist-search-submit,
          .stockist-search-button {
            width: 100% !important;
            min-width: 0 !important;
          }

          .stockist-map {
            height: min(88vw, 32rem) !important;
            min-height: 24rem !important;
          }

          .stockist-layout .stockist-result-list .stockist-list-result {
            padding: 16px 14px 12px 12px !important;
          }

          .stockist-layout .stockist-result-list .stockist-list-result::after {
            top: 22px;
            right: 12px;
          }

          .stockist-layout .stockist-result-list .stockist-list-result.stockist-selected::after {
            top: 27px;
          }

          .stockist-layout .stockist-result-list .stockist-result-name {
            margin-bottom: 8px !important;
          }

          .stockist-layout .stockist-result-list .stockist-result-address {
            margin-bottom: 10px !important;
          }

          .stockist-layout .stockist-result-list .stockist-result-address .bst-stockist-pin-link {
            padding-left: 24px !important;
          }

          .stockist-layout .stockist-result-list .stockist-result-details::before {
            margin-bottom: 4px;
          }

          .stockist-layout .stockist-result-list .stockist-result-website {
            margin-top: 6px;
          }

          .stockist-layout .stockist-result-list .bst-stockist-hours {
            margin-top: 12px;
            padding: 12px 10px 10px !important;
          }

          .stockist-layout .stockist-result-list .bst-stockist-hours__title {
            margin-bottom: 5px;
          }
        }
      `;
      root.prepend(style);
    }

    root.style.setProperty('display', isMobileViewport ? 'flex' : 'grid', 'important');
    root.style.setProperty('flex-direction', isMobileViewport ? 'column' : 'row', 'important');
    root.style.setProperty('grid-template-columns', isMobileViewport ? 'minmax(0, 1fr)' : '462px minmax(0, 1fr)', 'important');
    root.style.setProperty('grid-template-rows', isMobileViewport ? 'auto' : 'auto auto', 'important');
    root.style.setProperty('align-items', 'stretch', 'important');
    root.style.setProperty('position', 'relative', 'important');
    root.style.setProperty('overflow', isMobileViewport ? 'visible' : 'hidden', 'important');
    root.style.setProperty('isolation', 'isolate', 'important');
    root.style.setProperty('width', '100%', 'important');
    root.style.setProperty('min-width', '0', 'important');
    root.style.setProperty('max-width', 'none', 'important');
    root.style.setProperty('min-height', '0', 'important');
    root.style.setProperty('background', '#fff', 'important');

    return true;
  }

  function hasStableStockistTheme(root = getStockistLayoutRoot()) {
    if (!root) return false;

    const marker = root.querySelector(`style[data-${stockistThemeId}]`);
    if (!marker) return false;

    const computed = window.getComputedStyle(root);

    if (isMobileStockistViewport()) {
      return computed.display === 'flex';
    }

    return computed.display === 'grid' && computed.gridTemplateColumns.includes('462px');
  }

  function scheduleStockistThemeStabilization(reset = false) {
    if (reset) {
      themeStabilizationAttempts = 0;
    }

    window.clearTimeout(themeStabilizationTimer);

    const run = () => {
      ensureStockistTheme();
      enhanceStockistUi();
      ensureScrollWheelModifierGuard();

      if (hasStableStockistTheme() || themeStabilizationAttempts >= 18) {
        return;
      }

      const delay = activationBurstDelays[Math.min(themeStabilizationAttempts, activationBurstDelays.length - 1)];
      themeStabilizationAttempts += 1;
      themeStabilizationTimer = window.setTimeout(run, delay);
    };

    run();
  }

  function syncStockistLayoutStructure(root) {
    if (!root) return false;

    const form = root.querySelector('.stockist-search-form');
    const resultPanel = root.querySelector('.stockist-result-panel');
    const map = root.querySelector('.stockist-map');

    if (!form || !resultPanel || !map) return false;

    if (isMobileStockistViewport()) {
      if (form.nextElementSibling !== map) {
        form.insertAdjacentElement('afterend', map);
      }

      if (map.nextElementSibling !== resultPanel) {
        map.insertAdjacentElement('afterend', resultPanel);
      }

      return true;
    }

    if (form.nextElementSibling !== resultPanel) {
      form.insertAdjacentElement('afterend', resultPanel);
    }

    if (resultPanel.nextElementSibling !== map) {
      resultPanel.insertAdjacentElement('afterend', map);
    }

    return true;
  }

  function syncStockistContainerHeights(root) {
    if (!root) return false;

    const outerRoot = getStockistRootElement();
    const widgetHost = getStockistWidgetHost();

    if (isMobileStockistViewport()) {
      [
        outerRoot,
        widgetHost,
        root,
        root.querySelector('.stockist-result-panel'),
        root.querySelector('.stockist-results'),
        root.querySelector('.stockist-result-list'),
        root.querySelector('.stockist-result-list > ul')
      ].forEach((element) => {
        if (!element) return;
        element.style.setProperty('height', 'auto', 'important');
        element.style.setProperty('min-height', '0', 'important');
        element.style.setProperty('max-height', 'none', 'important');
        element.style.setProperty('overflow', 'visible', 'important');
      });

      return true;
    }

    const trackedNodes = [
      root.querySelector('.stockist-search-form'),
      root.querySelector('.stockist-result-panel'),
      root.querySelector('.stockist-map')
    ].filter(Boolean);

    const requiredHeight = Math.max(
      890,
      Math.ceil(
        trackedNodes.reduce((max, element) => {
          const box = element.getBoundingClientRect();
          return Math.max(max, element.offsetTop + box.height);
        }, 0)
      )
    );

    [outerRoot, widgetHost, root].forEach((element) => {
      if (!element) return;
      element.style.setProperty('height', `${requiredHeight}px`, 'important');
      element.style.setProperty('min-height', `${requiredHeight}px`, 'important');
    });

    return true;
  }

  function syncDesktopLocationHint(searchForm, searchFields, locationTrigger, isMobileViewport) {
    if (!searchForm || !searchFields) return null;

    let fallbackHint = searchForm.querySelector('.bst-stockist-search-location-hint');

    if (isMobileViewport) {
      if (locationTrigger) {
        fallbackHint?.remove();

        if (searchFields.nextElementSibling !== locationTrigger) {
          searchFields.insertAdjacentElement('afterend', locationTrigger);
        }

        return locationTrigger;
      }

      if (!fallbackHint) {
        fallbackHint = document.createElement('button');
        fallbackHint.type = 'button';
        fallbackHint.className = 'bst-stockist-search-location-hint';
        fallbackHint.textContent = 'Meinen Standort verwenden';
      }

      if (searchFields.nextElementSibling !== fallbackHint) {
        searchFields.insertAdjacentElement('afterend', fallbackHint);
      }

      return fallbackHint;
    }

    if (locationTrigger) {
      fallbackHint?.remove();

      if (searchFields.lastElementChild !== locationTrigger) {
        searchFields.appendChild(locationTrigger);
      }

      return locationTrigger;
    }

    if (!fallbackHint) {
      fallbackHint = document.createElement('button');
      fallbackHint.type = 'button';
      fallbackHint.className = 'bst-stockist-search-location-hint';
      fallbackHint.textContent = 'Meinen Standort verwenden';
    }

    if (searchFields.lastElementChild !== fallbackHint) {
      searchFields.appendChild(fallbackHint);
    }

    return fallbackHint;
  }

  function ensureLocationTriggerLabel(trigger) {
    if (!trigger) return null;

    const label = 'Meinen Standort verwenden';
    const hasDirectText = Array.from(trigger.childNodes).some(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.replace(/\s+/g, ' ').trim().length > 0
    );
    const hasVisibleChildText = Array.from(trigger.querySelectorAll('*')).some((node) => {
      if (!node.textContent.replace(/\s+/g, ' ').trim()) return false;

      const computedStyle = window.getComputedStyle(node);
      return computedStyle.display !== 'none'
        && computedStyle.visibility !== 'hidden'
        && computedStyle.fontSize !== '0px';
    });

    trigger.dataset.bstLocationLabel = label;
    trigger.setAttribute('aria-label', label);
    trigger.classList.toggle(
      'bst-stockist-location-trigger--label-fallback',
      !hasDirectText && !hasVisibleChildText && !trigger.classList.contains('bst-stockist-search-location-hint')
    );

    return trigger;
  }

  function ensureLocationTriggerBehavior(trigger) {
    if (!trigger || trigger.dataset.bstLocationTriggerBound === 'true') return trigger;

    trigger.dataset.bstLocationTriggerBound = 'true';

    trigger.addEventListener('click', async (event) => {
      event.preventDefault();

      const previousDisabled = trigger.getAttribute('aria-disabled');
      trigger.setAttribute('aria-disabled', 'true');
      trigger.dataset.bstLocating = 'true';

      try {
        await triggerCurrentLocationSearch();
      } catch (error) {
        console.warn('Current location lookup failed', error);
      } finally {
        trigger.dataset.bstLocating = 'false';

        if (previousDisabled) {
          trigger.setAttribute('aria-disabled', previousDisabled);
        } else {
          trigger.removeAttribute('aria-disabled');
        }
      }
    });

    return trigger;
  }

  function enhanceStockistUi() {
    const root = getStockistLayoutRoot();
    if (!root) return false;
    const isMobileViewport = isMobileStockistViewport();
    const useStackedMobileSearch = Math.max(320, document.documentElement.clientWidth || window.innerWidth) < 750;
    syncStockistLayoutStructure(root);

    const searchForm = root.querySelector('.stockist-search-form');
    const searchFields = root.querySelector('.stockist-search-fields-horizontal');
    const searchQuery = root.querySelector('.stockist-search-query');
    const searchWrapper = root.querySelector('.stockist-search-wrapper');
    const searchField = root.querySelector('.stockist-search-field');
    const locationTrigger = root.querySelector('.stockist-search-by-location');

    if (searchForm) {
      searchForm.classList.toggle('bst-stockist-search-form--has-location', Boolean(locationTrigger));
      searchForm.style.setProperty('flex-wrap', isMobileViewport ? 'wrap' : 'nowrap', 'important');
      searchForm.style.setProperty('justify-content', isMobileViewport ? 'stretch' : 'flex-start', 'important');
      searchForm.style.setProperty('gap', isMobileViewport ? '12px' : '16px', 'important');
      searchForm.style.setProperty('padding', isMobileViewport ? '16px' : '12px 16px', 'important');
      searchForm.style.setProperty('min-height', isMobileViewport ? '0' : '68px', 'important');
      searchForm.style.setProperty('color', '#fff', 'important');
    }

    if (searchFields) {
      searchFields.style.setProperty('display', isMobileViewport ? 'grid' : 'flex', 'important');
      searchFields.style.setProperty('flex-direction', 'row', 'important');
      searchFields.style.setProperty('flex-wrap', 'nowrap', 'important');
      searchFields.style.setProperty('align-items', 'center', 'important');
      searchFields.style.setProperty('gap', isMobileViewport ? '10px' : '10px', 'important');
      searchFields.style.setProperty('flex', '1 1 100%', 'important');
      searchFields.style.setProperty('width', '100%', 'important');
      searchFields.style.setProperty('max-width', 'none', 'important');
      searchFields.style.setProperty('height', isMobileViewport ? 'auto' : '44px', 'important');
      searchFields.style.setProperty('min-height', isMobileViewport ? '0' : '44px', 'important');
      searchFields.style.setProperty('block-size', isMobileViewport ? 'auto' : '44px', 'important');
      searchFields.style.setProperty('min-block-size', isMobileViewport ? '0' : '44px', 'important');
      searchFields.style.setProperty('max-block-size', isMobileViewport ? 'none' : '44px', 'important');

      if (isMobileViewport) {
        searchFields.style.setProperty(
          'grid-template-columns',
          useStackedMobileSearch ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(136px, 45%)',
          'important'
        );
      } else {
        searchFields.style.removeProperty('grid-template-columns');
      }
    }

    if (searchQuery) {
      searchQuery.style.setProperty('flex', isMobileViewport ? '1 1 auto' : '0 1 33%', 'important');
      searchQuery.style.setProperty('width', isMobileViewport ? 'auto' : '33%', 'important');
      searchQuery.style.setProperty('max-width', isMobileViewport ? 'none' : '33%', 'important');
      searchQuery.style.setProperty('height', '44px', 'important');
      searchQuery.style.setProperty('min-height', '44px', 'important');
      searchQuery.style.setProperty('block-size', '44px', 'important');
      searchQuery.style.setProperty('min-block-size', '44px', 'important');
      searchQuery.style.setProperty('max-block-size', '44px', 'important');
    }

    [searchWrapper, searchField].forEach((element) => {
      if (!element) return;

      element.style.setProperty('flex', '1 1 auto', 'important');
      element.style.setProperty('width', '100%', 'important');
      element.style.setProperty('max-width', '100%', 'important');
      element.style.setProperty('height', '44px', 'important');
      element.style.setProperty('min-height', '44px', 'important');
      element.style.setProperty('block-size', '44px', 'important');
      element.style.setProperty('min-block-size', '44px', 'important');
      element.style.setProperty('max-block-size', '44px', 'important');
    });

    if (isMobileViewport && locationTrigger && searchFields && locationTrigger.previousElementSibling !== searchFields) {
      searchFields.insertAdjacentElement('afterend', locationTrigger);
    }

    const activeLocationTrigger = ensureLocationTriggerLabel(
      syncDesktopLocationHint(searchForm, searchFields, locationTrigger, isMobileViewport)
    );
    searchForm?.classList.toggle('bst-stockist-search-form--has-location', Boolean(activeLocationTrigger));

    if (activeLocationTrigger?.classList?.contains('bst-stockist-search-location-hint')) {
      ensureLocationTriggerBehavior(activeLocationTrigger);
    }

    const searchInput = root.querySelector('.stockist-search-field input');
    if (searchInput) {
      searchInput.placeholder = 'Adresse, PLZ, Stadt';
      searchInput.setAttribute('aria-label', 'Adresse, PLZ, Stadt');
      searchInput.style.setProperty('width', '100%', 'important');
      searchInput.style.setProperty('height', '44px', 'important');
      searchInput.style.setProperty('min-height', '44px', 'important');
      searchInput.style.setProperty('block-size', '44px', 'important');
      searchInput.style.setProperty('min-block-size', '44px', 'important');
      searchInput.style.setProperty('box-sizing', 'border-box', 'important');
      searchInput.style.setProperty('padding', '0 14px', 'important');
      searchInput.style.setProperty('border-radius', 'var(--bst-radius-md, 7px)', 'important');
      searchInput.style.setProperty('font-family', '"Work Sans", Helvetica, Arial, sans-serif', 'important');
      searchInput.style.setProperty('font-size', '15px', 'important');
      searchInput.style.setProperty('font-weight', '400', 'important');
      searchInput.style.setProperty('line-height', '1.2', 'important');
    }

    const searchButtonText = root.querySelector('.stockist-search-button-text');
    if (searchButtonText) {
      searchButtonText.textContent = "{{ 'general.search.placeholder' | t }}";
    }

    const searchButton = root.querySelector('.stockist-search-button');
    if (searchButton) {
      searchButton.style.setProperty('background', 'var(--bst-color-blue-500, #004f9e)', 'important');
      searchButton.style.setProperty('width', isMobileViewport ? '100%' : '148px', 'important');
      searchButton.style.setProperty('min-width', isMobileViewport ? '0' : '148px', 'important');
      searchButton.style.setProperty('height', '44px', 'important');
      searchButton.style.setProperty('border-radius', 'var(--bst-radius-md, 7px)', 'important');
      searchButton.style.setProperty('box-shadow', 'none', 'important');
    }

    const searchSubmit = root.querySelector('.stockist-search-submit');
    if (searchSubmit) {
      searchSubmit.style.setProperty('margin', isMobileViewport ? '0' : '0', 'important');
      searchSubmit.style.setProperty('width', isMobileViewport ? 'auto' : '148px', 'important');
      searchSubmit.style.setProperty('flex', isMobileViewport ? '0 1 auto' : '0 0 148px', 'important');
    }

    const resultPanel = root.querySelector('.stockist-result-panel');
    const map = root.querySelector('.stockist-map');
    if (resultPanel && map) {
      resultPanel.style.setProperty('order', isMobileViewport ? '3' : '0', 'important');
      map.style.setProperty('order', isMobileViewport ? '2' : '0', 'important');
    }

    syncStockistContainerHeights(root);

    root.querySelectorAll('.stockist-list-result').forEach((item) => {
      const websiteElement = item.querySelector('.stockist-result-website');
      const currentWebsiteLink = websiteElement?.querySelector('a');
      const storedSourceUrl = websiteElement?.dataset.bstSourceUrl;
      const currentWebsiteHref = currentWebsiteLink?.getAttribute('href');
      const rawUrl = storedSourceUrl
        || (currentWebsiteHref && currentWebsiteHref !== 'Gehe zur Seite' ? currentWebsiteHref : '')
        || (websiteElement?.textContent || '').trim();
      const targetUrl = normalizePath(toShopifyFilialeUrl(rawUrl));
      const nameElement = item.querySelector('.stockist-result-name');
      const addressElement = item.querySelector('.stockist-result-address');
      const directionsHref = item.querySelector('.stockist-result-directions-link a')?.getAttribute('href');
      const city = item.querySelector('.stockist-address-city')?.textContent?.trim();
      const postalCode = item.querySelector('.stockist-address-postal-code')?.textContent?.trim();

      if (postalCode) item.dataset.bstPostalCode = postalCode;
      const localityElement = item.querySelector('.stockist-result-addr-locality');
      const phoneElement = item.querySelector('.stockist-result-phone');
      const emailElement = item.querySelector('.stockist-result-email');

      if (websiteElement && targetUrl) {
        websiteElement.dataset.bstSourceUrl = rawUrl;
        websiteElement.dataset.bstTargetUrl = targetUrl;

        if (currentWebsiteLink?.getAttribute('href') !== targetUrl || currentWebsiteLink.textContent !== 'Gehe zur Seite') {
          websiteElement.textContent = '';
          const link = document.createElement('a');
          link.href = targetUrl;
          link.textContent = 'Gehe zur Seite';
          websiteElement.appendChild(link);
        }
      }

      if (nameElement && targetUrl) {
        let titleLink = nameElement.querySelector('a');

        if (!titleLink) {
          const titleText = nameElement.textContent.trim();
          nameElement.textContent = '';
          titleLink = document.createElement('a');
          titleLink.textContent = titleText;
          nameElement.appendChild(titleLink);
        }

        titleLink.href = targetUrl;
      }

      if (addressElement && directionsHref && !addressElement.querySelector('.bst-stockist-pin-link')) {
        const pinLink = document.createElement('a');
        pinLink.className = 'bst-stockist-pin-link';
        pinLink.href = directionsHref;
        pinLink.target = '_blank';
        pinLink.rel = 'noopener';

        while (addressElement.firstChild) {
          pinLink.appendChild(addressElement.firstChild);
        }

        addressElement.appendChild(pinLink);
      }

      if (localityElement && city && postalCode) {
        localityElement.textContent = `${postalCode} ${city}`;
      }

      if (phoneElement) {
        phoneElement.dataset.bstLabel = 'Telefon';
        decorateDetailListItem(phoneElement, 'Telefon');
      }

      if (emailElement) {
        emailElement.dataset.bstLabel = 'E-Mail';
        decorateDetailListItem(emailElement, 'E-Mail');
      }

      if (websiteElement) {
        websiteElement.dataset.bstLabel = '';
      }

      if (!nameElement || !city || !postalCode) return;

      const rawName = nameElement.textContent.trim();
      const lowerName = rawName.toLowerCase();
      let nextLabel = `${postalCode} ${city}`;

      if (lowerName.includes('schuhshop')) {
        nextLabel = `${postalCode} ${city}, Schuhshop`;
      } else if (lowerName.includes('institut')) {
        nextLabel = `${postalCode} ${city}, Institut Bständig e.U.`;
      } else if (lowerName.startsWith('lk ')) {
        nextLabel = `${postalCode} ${city}, ${rawName}`;
      }

      const titleLink = nameElement.querySelector('a');

      if (titleLink) {
        titleLink.textContent = nextLabel;
      } else {
        nameElement.textContent = nextLabel;
      }
    });

    sortStockistResults(root);

    return true;
  }

  // Ohne Umkreissuche folgt die Liste dem Filialen-Akkordeon, auf Seiten ohne Akkordeon der PLZ.
  function sortStockistResults(root) {
    const list = root.querySelector('.stockist-result-list > ul');

    if (!list) return;

    const items = Array.from(list.querySelectorAll(':scope > .stockist-list-result'));

    if (items.length < 2) return;

    const hasDistance = items.some(
      (item) => (item.querySelector('.stockist-result-distance')?.textContent || '').trim() !== ''
    );

    if (hasDistance) return;

    const accordionOrder = Array.from(document.querySelectorAll('.bst-filialen-page__location-link'))
      .map((link) => normalizePath(new URL(link.href, window.location.origin).pathname));

    const entries = items.map((item, index) => {
      const target = item.querySelector('[data-bst-target-url]')?.dataset.bstTargetUrl || '';
      const accordionIndex = accordionOrder.indexOf(target);
      const postal = Number((item.dataset.bstPostalCode || '').replace(/\D/g, ''));

      return {
        item,
        index,
        position: accordionIndex === -1 ? Number.MAX_SAFE_INTEGER : accordionIndex,
        postal: postal || Number.MAX_SAFE_INTEGER,
      };
    });

    const sorted = entries.slice().sort(
      (a, b) => (a.position - b.position) || (a.postal - b.postal) || (a.index - b.index)
    );

    if (sorted.every((entry, position) => entry.index === position)) return;

    const fragment = document.createDocumentFragment();

    sorted.forEach((entry) => fragment.appendChild(entry.item));
    list.appendChild(fragment);
  }

  function parseLocationDetailsFromDocument(doc) {
    const inner = doc.querySelector('.bst-main-page__content .section--teaser-text-image .teaser-text-image__content__inner')
      || doc.querySelector('.section--teaser-text-image .teaser-text-image__content__inner');

    if (!inner) return null;

    const table = inner.querySelector('table');
    const routeLink = Array.from(inner.querySelectorAll('a')).find((link) => /Ihr Weg zu unserer bständig Filiale/i.test(link.textContent || ''));
    const details = {
      phone: null,
      fax: null,
      email: null,
      hours: [],
      routeHref: routeLink?.getAttribute('href') || null
    };

    if (!table) return details;

    let inHours = false;
    Array.from(table.querySelectorAll('tr')).forEach((row) => {
      const cells = Array.from(row.querySelectorAll('td'));
      if (cells.length < 2) return;

      const label = cells[0].textContent.replace(/\s+/g, ' ').trim();
      const valueCell = cells[1];
      const value = valueCell.textContent.replace(/\s+/g, ' ').trim();

      if (!label && !value) return;

      if (/^Kontakt$/i.test(label)) {
        inHours = false;
        return;
      }

      if (/^Öffnungszeiten$/i.test(label)) {
        inHours = true;
        return;
      }

      if (inHours) {
        if (label && value) {
          details.hours.push({ label, value });
        }
        return;
      }

      if (/^Telefon$/i.test(label)) {
        details.phone = value || null;
      } else if (/^Fax$/i.test(label)) {
        details.fax = value || null;
      } else if (/^E-?Mail$/i.test(label)) {
        const emailLink = valueCell.querySelector('a[href^="mailto:"]');
        details.email = emailLink?.textContent?.trim() || value || null;
      }
    });

    return details;
  }

  async function fetchLocationDetails(targetUrl) {
    const normalizedTargetUrl = normalizePath(targetUrl);
    if (!normalizedTargetUrl) return null;

    if (locationDetailCache.has(normalizedTargetUrl)) {
      return locationDetailCache.get(normalizedTargetUrl);
    }

    const detailPromise = (async () => {
      try {
        if (normalizePath(window.location.pathname) === normalizedTargetUrl) {
          return parseLocationDetailsFromDocument(document);
        }

        const response = await fetch(normalizedTargetUrl, { credentials: 'same-origin' });
        if (!response.ok) return null;

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        return parseLocationDetailsFromDocument(parsed);
      } catch (error) {
        console.warn('Failed to fetch location details', normalizedTargetUrl, error);
        return null;
      }
    })();

    locationDetailCache.set(normalizedTargetUrl, detailPromise);
    return detailPromise;
  }

  function ensureDetailListItem(list, className, label) {
    let item = list.querySelector(`.${className}`);

    if (!item) {
      item = document.createElement('li');
      item.className = className;
      list.insertBefore(item, list.querySelector('.stockist-result-website') || null);
    }

    item.dataset.bstLabel = label;
    return item;
  }

  function decorateDetailListItem(item, label) {
    if (!item || !label) return;

    const existingLabel = item.querySelector('.bst-stockist-detail-label');
    const existingValue = item.querySelector('.bst-stockist-detail-value');

    if (existingLabel && existingValue) {
      existingLabel.textContent = `${label}:`;
      return;
    }

    const value = document.createElement('span');
    value.className = 'bst-stockist-detail-value';

    while (item.firstChild) {
      value.appendChild(item.firstChild);
    }

    const labelNode = document.createElement('span');
    labelNode.className = 'bst-stockist-detail-label';
    labelNode.textContent = `${label}:`;

    item.append(labelNode, value);
  }

  function reorderDetailItems(detailsList) {
    if (!detailsList) return;

    const order = [
      '.stockist-result-phone',
      '.bst-stockist-fax',
      '.stockist-result-email',
      '.stockist-result-website'
    ];

    order.forEach((selector) => {
      const item = detailsList.querySelector(selector);
      if (item) {
        detailsList.appendChild(item);
      }
    });
  }

  function renderHoursBlock(parent, details) {
    let hoursBlock = parent.querySelector('.bst-stockist-hours');

    if (!details?.hours?.length) {
      hoursBlock?.remove();
      return;
    }

    if (!hoursBlock) {
      hoursBlock = document.createElement('div');
      hoursBlock.className = 'bst-stockist-hours';
      parent.appendChild(hoursBlock);
    }

    hoursBlock.textContent = '';

    const title = document.createElement('strong');
    title.className = 'bst-stockist-hours__title';
    title.textContent = 'Öffnungszeiten:';
    hoursBlock.appendChild(title);

    details.hours.forEach((row) => {
      const rowNode = document.createElement('div');
      rowNode.className = 'bst-stockist-hours__row';

      const label = document.createElement('span');
      label.className = 'bst-stockist-hours__label';
      label.textContent = row.label;

      const value = document.createElement('span');
      value.className = 'bst-stockist-hours__value';
      value.textContent = row.value;

      rowNode.append(label, value);
      hoursBlock.appendChild(rowNode);
    });
  }

  function enrichListItemWithDetails(item, details) {
    if (!item || !details) return;

    const detailsList = item.querySelector('.stockist-result-details');
    if (!detailsList) return;

    if (details.phone) {
      const phoneItem = ensureDetailListItem(detailsList, 'stockist-result-phone', 'Telefon');
      let phoneLink = phoneItem.querySelector('a');

      if (!phoneLink) {
        phoneLink = document.createElement('a');
        phoneItem.textContent = '';
        phoneItem.appendChild(phoneLink);
      }

      phoneLink.href = `tel:${details.phone}`;
      phoneLink.textContent = details.phone;
      decorateDetailListItem(phoneItem, 'Telefon');
    }

    if (details.fax) {
      const faxItem = ensureDetailListItem(detailsList, 'bst-stockist-fax', 'Fax');
      faxItem.textContent = details.fax;
      decorateDetailListItem(faxItem, 'Fax');
    }

    if (details.email) {
      const emailItem = ensureDetailListItem(detailsList, 'stockist-result-email', 'E-Mail');
      let emailLink = emailItem.querySelector('a');

      if (!emailLink) {
        emailLink = document.createElement('a');
        emailItem.textContent = '';
        emailItem.appendChild(emailLink);
      }

      emailLink.href = `mailto:${details.email}`;
      emailLink.textContent = details.email;
      decorateDetailListItem(emailItem, 'E-Mail');
    }

    reorderDetailItems(detailsList);
    renderHoursBlock(item, details);
  }

  function enrichStockistDetails() {
    const root = getStockistLayoutRoot();
    if (!root) return false;

    root.querySelectorAll('.stockist-list-result').forEach((item) => {
      const targetUrl = item.querySelector('.stockist-result-website')?.dataset.bstTargetUrl
        || item.querySelector('.stockist-result-website a')?.getAttribute('href');

      if (!targetUrl || item.dataset.bstDetailsLoading === 'true' || item.dataset.bstDetailsLoaded === 'true') {
        return;
      }

      item.dataset.bstDetailsLoading = 'true';

      fetchLocationDetails(targetUrl).then((details) => {
        if (details) {
          enrichListItemWithDetails(item, details);
          item.dataset.bstDetailsLoaded = 'true';
        }
      }).finally(() => {
        delete item.dataset.bstDetailsLoading;
      });
    });

    return true;
  }

  function scrollListItemIntoView(root, listItem) {
    if (!root || !listItem) return;

    const scrollContainer = root.querySelector('.stockist-results');
    if (!scrollContainer) return;

    const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;
    if (maxScroll <= 0) return;

    const containerRect = scrollContainer.getBoundingClientRect();
    const itemRect = listItem.getBoundingClientRect();
    const delta = itemRect.top - containerRect.top - (scrollContainer.clientHeight - itemRect.height) / 2;
    const nextScrollTop = Math.min(maxScroll, Math.max(0, scrollContainer.scrollTop + delta));

    scrollContainer.scrollTop = nextScrollTop;
  }

  function syncCurrentLocationListState(currentLocation) {
    const root = getStockistRootElement();
    if (!root || !currentLocation?.id) return false;

    root.querySelectorAll('.stockist-list-result.stockist-selected').forEach((item) => {
      item.classList.remove('stockist-selected');
      item.removeAttribute('aria-current');
    });

    const currentItem = root.querySelector(`.stockist-list-result[data-stockist-location="${currentLocation.id}"]`);
    if (!currentItem) return false;

    currentItem.classList.add('stockist-selected');
    currentItem.setAttribute('aria-current', 'true');
    scrollListItemIntoView(root, currentItem);
    return true;
  }

  function focusCurrentLocationOnMap(mapInstance, markerLatLng) {
    if (!mapInstance || !markerLatLng) return;

    const currentZoom = typeof mapInstance.getZoom === 'function' ? mapInstance.getZoom() : 0;
    const maxZoom = typeof mapInstance.getMaxZoom === 'function' ? mapInstance.getMaxZoom() : 18;
    const targetZoom = Math.min(maxZoom || 18, Math.max(currentZoom || 0, 15));

    if (typeof mapInstance.invalidateSize === 'function') {
      mapInstance.invalidateSize(false);
    }

    if (typeof mapInstance.setView === 'function') {
      mapInstance.setView(markerLatLng, targetZoom, { animate: false });
      return;
    }

    if (typeof mapInstance.panTo === 'function') {
      mapInstance.panTo(markerLatLng, { animate: false });
    }
  }

  async function hydrateLegacyLocator() {
    const currentHandle = getCurrentPageHandle();
    if (!currentHandle) return;

    if (document.querySelector('.bst-main-page .bst-stockist-locator stockist-store-locator, .bst-main-page stockist-store-locator')) {
      return;
    }

    const legacyLocator = ensureLocatorMount();
    if (!legacyLocator) return;

    const locatorSection = legacyLocator.closest('.section--store-locator');
    locatorSection?.classList.add('bst-stockist-loading');

    try {
      const { stockistWidget, stockistScriptSrc } = await loadStockistSourceMarkup();

      const widgetShell = document.createElement('div');
      widgetShell.className = 'bst-stockist-locator';
      widgetShell.appendChild(stockistWidget.cloneNode(true));

      mountStockistWidget(legacyLocator, widgetShell);
      ensureStockistScript(stockistScriptSrc);

      locatorSection?.classList.remove('bst-stockist-loading');
      locatorSection?.classList.add('bst-stockist-ready');
    } catch (error) {
      locatorSection?.classList.remove('bst-stockist-loading');
      locatorSection?.classList.add('bst-stockist-error');
      throw error;
    }
  }

  async function activateCurrentLocation(options = {}) {
    const { force = false } = options;
    const currentDetailPath = getCurrentDetailPath();
    if (!currentDetailPath || (currentLocationActivated && !force)) return true;

    const widget = window.Stockist?.__widget;
    const service = widget?._client?._service;
    const store = widget?._client?._store;
    const map = widget?._map;

    if (!service || !store || !map) return false;

    const locations = await service.getAllLocations();
    const currentLocation = locations.find(
      (location) => normalizePath(toShopifyFilialeUrl(location.website)) === currentDetailPath
    );

    if (!currentLocation) return false;

    const marker = map._markerMap?.[currentLocation.id];
    if (!marker || typeof marker.getLatLng !== 'function') return false;

    store.setSelectedLocation(currentLocation);

    if (typeof map._onLocationSelected === 'function') {
      await Promise.resolve(map._onLocationSelected(currentLocation));
    }

    syncCurrentLocationListState(currentLocation);

    const markerLatLng = marker.getLatLng();

    if (markerLatLng && map._map) {
      focusCurrentLocationOnMap(map._map, markerLatLng);
    }

    if (!map._rootElement?.querySelector('.leaflet-popup') && typeof map._openPopup === 'function') {
      map._openPopup(currentLocation);
    }

    currentLocationActivated = true;
    return true;
  }

  function scheduleCurrentLocationActivation(attempt = 0) {
    if (currentLocationActivated || !getCurrentDetailPath()) return;

    clearTimeout(activationTimer);
    activationTimer = window.setTimeout(async () => {
      try {
        const activated = await activateCurrentLocation();
        if (!activated && attempt < 12) {
          scheduleCurrentLocationActivation(attempt + 1);
        }
      } catch (error) {
        if (attempt < 12) {
          scheduleCurrentLocationActivation(attempt + 1);
        } else {
          console.warn('Stockist current-location activation failed', error);
        }
      }
    }, attempt === 0 ? 50 : 350);
  }

  function primeCurrentLocationActivationBurst() {
    if (activationBurstPrimed || !getCurrentDetailPath()) return;

    activationBurstPrimed = true;

    activationBurstDelays.forEach((delay) => {
      window.setTimeout(() => {
        ensureStockistTheme();
        enhanceStockistUi();
        enrichStockistDetails();
        ensureScrollWheelModifierGuard();
        activateCurrentLocation({ force: true }).catch((error) => {
          console.warn('Stockist current-location sync retry failed', error);
        });
      }, delay);
    });
  }

  function primeStockistThemeBurst() {
    if (themeBurstPrimed) return;

    themeBurstPrimed = true;

    activationBurstDelays.forEach((delay) => {
      window.setTimeout(() => {
        scheduleStockistThemeStabilization();
      }, delay);
    });
  }

  function bootstrapStockist() {
    rewriteLegacyFilialeLinks(document);

    hydrateLegacyLocator().catch((error) => {
      console.error('Failed to hydrate Stockist on location page', error);
    });

    ensureStockistTheme();
    enhanceStockistUi();
    enrichStockistDetails();
    ensureScrollWheelModifierGuard();
    scheduleStockistThemeStabilization(true);
    scheduleCurrentLocationActivation();
    primeStockistThemeBurst();
    primeCurrentLocationActivationBurst();
  }

  window.addEventListener('resize', () => {
    window.clearTimeout(window.__bstStockistResizeTimer);
    window.__bstStockistResizeTimer = window.setTimeout(() => {
      scheduleStockistThemeStabilization(true);
    }, 120);
  });

  window.addEventListener('load', () => {
    scheduleStockistThemeStabilization(true);
  });

  window.__bstToShopifyFilialeUrl = toShopifyFilialeUrl;

  window.__stockist_widget_resultsreceived = function stockistResultsReceived(payload) {
    let nextPayload = payload;

    if (typeof previousResultsReceived === 'function') {
      const returned = previousResultsReceived(payload);
      if (returned) nextPayload = returned;
    }

    return rewriteResultsPayload(nextPayload);
  };

  window.__stockist_widget_domloaded = function stockistDomLoaded(...args) {
    try {
      if (typeof previousDomLoaded === 'function') {
        previousDomLoaded(...args);
      }
    } catch (error) {
      console.warn('Previous Stockist domloaded handler failed', error);
    }

    stockistRootElement = args.find((arg) => arg?.nodeType === 1) || stockistRootElement;

    rewriteLegacyFilialeLinks(document);
    scheduleStockistThemeStabilization(true);
    enrichStockistDetails();
    ensureScrollWheelModifierGuard();
    scheduleCurrentLocationActivation();
    primeStockistThemeBurst();
    primeCurrentLocationActivationBurst();
  };

  window.__stockist_widget_listchanged = function stockistListChanged(...args) {
    try {
      if (typeof previousListChanged === 'function') {
        previousListChanged(...args);
      }
    } catch (error) {
      console.warn('Previous Stockist listchanged handler failed', error);
    }

    rewriteLegacyFilialeLinks(document);
    scheduleStockistThemeStabilization(true);
    enrichStockistDetails();
    ensureScrollWheelModifierGuard();
    scheduleCurrentLocationActivation();
    primeStockistThemeBurst();
    primeCurrentLocationActivationBurst();
  };

  window.__stockist_widget_mapcreated = function stockistMapCreated(...args) {
    try {
      if (typeof previousMapCreated === 'function') {
        previousMapCreated(...args);
      }
    } catch (error) {
      console.warn('Previous Stockist mapcreated handler failed', error);
    }

    rewriteLegacyFilialeLinks(document);
    scheduleStockistThemeStabilization(true);
    enrichStockistDetails();
    ensureScrollWheelModifierGuard();
    scheduleCurrentLocationActivation();
    primeStockistThemeBurst();
    primeCurrentLocationActivationBurst();
  };

  window.__stockist_widget_location_selected = function stockistLocationSelected(payload) {
    try {
      if (typeof previousLocationSelected === 'function') {
        previousLocationSelected(payload);
      }
    } catch (error) {
      console.warn('Previous Stockist location handler failed', error);
    }

    const targetUrl = toShopifyFilialeUrl(payload?.location?.website);

    if (targetUrl && targetUrl !== window.location.pathname) {
      window.location.assign(targetUrl);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapStockist, { once: true });
  } else {
    bootstrapStockist();
  }
})();
